import { T } from '@/util/Localize';

/*
 * The cart weather hint (ROBO-846) — which days get an icon, and how the tooltip words them.
 *
 * Legacy has no counterpart to port: ROBO-846 adds the same rules to `View/js/cart-gcal.js` and
 * `View/js/cart-apptpref.js` at the same time. The wind threshold and the units are Herb's rulings of
 * 2026-10-24 on that ticket; the precipitation threshold is the calendar module's own setting:
 *   - rain icon:  the higher of `chanceOfRain` and `chanceOfSnow` at or above the module's
 *                 `weatherprecip`, 50 unless set; the tooltip says "rain" or "snow" by whichever
 *                 chance is higher;
 *   - wind icon:  `maxWindMph >= 15` (24 km/h) — always tested in mph, whatever the brand's units;
 *   - units:      the brand's country. °F / mph for the US, °C / km/h for everyone else.
 *
 * ★ NEVER CONVERT. The forecast route sends both scales (`maxTempF` / `maxTempC`, `maxWindMph` /
 *   `maxWindKph`); these functions only pick one and round it for display.
 */

/** One daily row of `POST checkout/weather/forecast` (ROBO-830), `data.days[]`. */
export interface WeatherDay {
    /** Location-local `yyyy-MM-dd`. */
    date: string;
    maxTempF?: number;
    maxTempC?: number;
    minTempF?: number;
    minTempC?: number;
    maxWindMph?: number;
    maxWindKph?: number;
    chanceOfRain: number;
    chanceOfSnow: number;
    condition?: { text?: string; code?: number };
}

/** `POST checkout/weather/forecast` → `data`. Hourly rows are never asked for here. */
export interface WeatherForecast {
    timeZone?: string;
    hours?: unknown[];
    days: WeatherDay[];
    forecastDaysAvailable?: number;
}

export type WeatherUnits = 'imperial' | 'metric';

/** What a calendar is handed: the forecast by ISO date, the brand's units, and the module's threshold. */
export interface CalendarWeather {
    days: Readonly<Record<string, WeatherDay>>;
    units: WeatherUnits;
    precipThreshold: number;
}

/** A chance of rain OR snow at or above this, in percent, shows the rain icon unless the module sets its own. */
export const DEFAULT_PRECIP_THRESHOLD_PERCENT = 50;
/** A max wind at or above this, in mph, shows the wind icon. */
export const WIND_THRESHOLD_MPH = 15;

/** Brand country ids (`tblBrand.nBrandCountryID`) and the API's names for them. */
const US_COUNTRY_ID = 1;
const US_COUNTRY_NAMES = ['usa', 'us'];

/**
 * The brand's units: imperial for the US (country 1), metric for every other country (2 Canada,
 * 3 Australia, …). Takes the numeric id or the name the API sends (`'usa'`, `'canada'`, …).
 *
 * Nothing known at all (`undefined`, `null`, `''`) reads as the US, the same default
 * `util/constants#getCountry` applies to a brand with no country.
 */
export function unitsForCountry(country: number | string | null | undefined): WeatherUnits {
    if (country === undefined || country === null || country === '') return 'imperial';
    if (typeof country === 'number') return country === US_COUNTRY_ID ? 'imperial' : 'metric';
    const name = country.trim().toLowerCase();
    if (/^\d+$/.test(name)) return Number(name) === US_COUNTRY_ID ? 'imperial' : 'metric';
    return US_COUNTRY_NAMES.includes(name) ? 'imperial' : 'metric';
}

export function isRainy(
    day: WeatherDay,
    precipThreshold: number = DEFAULT_PRECIP_THRESHOLD_PERCENT
): boolean {
    return (day.chanceOfRain ?? 0) >= precipThreshold || (day.chanceOfSnow ?? 0) >= precipThreshold;
}

export function isWindy(day: WeatherDay): boolean {
    return typeof day.maxWindMph === 'number' && day.maxWindMph >= WIND_THRESHOLD_MPH;
}

/** Whether the day gets any icon at all. */
export function hasWeatherFlag(
    day: WeatherDay | undefined,
    precipThreshold: number = DEFAULT_PRECIP_THRESHOLD_PERCENT
): day is WeatherDay {
    return day !== undefined && (isRainy(day, precipThreshold) || isWindy(day));
}

/** Which precipitation the tooltip names: snow only when its chance is strictly the higher one. */
export function precipitationKind(day: WeatherDay): 'rain' | 'snow' {
    return (day.chanceOfSnow ?? 0) > (day.chanceOfRain ?? 0) ? 'snow' : 'rain';
}

/** The chance the tooltip quotes — the higher of the two, as `precipitationKind` names it. */
export function precipitationChance(day: WeatherDay): number {
    return Math.max(day.chanceOfRain ?? 0, day.chanceOfSnow ?? 0);
}

function rounded(value: number | undefined): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : undefined;
}

/** `68°F` or `20°C`; `undefined` when the forecast left that number out. */
export function formatTemperature(
    fahrenheit: number | undefined,
    celsius: number | undefined,
    units: WeatherUnits
): string | undefined {
    const value = rounded(units === 'imperial' ? fahrenheit : celsius);
    if (value === undefined) return undefined;
    return `${value}°${units === 'imperial' ? 'F' : 'C'}`;
}

export function formatHigh(day: WeatherDay, units: WeatherUnits): string | undefined {
    return formatTemperature(day.maxTempF, day.maxTempC, units);
}

export function formatLow(day: WeatherDay, units: WeatherUnits): string | undefined {
    return formatTemperature(day.minTempF, day.minTempC, units);
}

/** `18 mph` or `29 km/h`; `undefined` when the forecast left it out. */
export function formatWind(day: WeatherDay, units: WeatherUnits): string | undefined {
    const value = rounded(units === 'imperial' ? day.maxWindMph : day.maxWindKph);
    if (value === undefined) return undefined;
    return `${value} ${units === 'imperial' ? 'mph' : 'km/h'}`;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];

/** `Tuesday, March 30` — the same spoken day name both calendars use. */
export function formatWeatherDate(date: string): string {
    const [year, month, day] = date.split('-').map(Number);
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    return `${T(WEEKDAYS[weekday])}, ${T(MONTHS[month - 1])} ${day}`;
}

/** The forecast's rows by ISO date. A row without a date is dropped; a repeated date keeps the first. */
export function weatherDaysByDate(
    days: readonly WeatherDay[] | undefined
): Record<string, WeatherDay> {
    const byDate: Record<string, WeatherDay> = {};
    for (const day of days ?? []) {
        if (day && typeof day.date === 'string' && !(day.date in byDate)) byDate[day.date] = day;
    }
    return byDate;
}

/**
 * The forecast for one calendar day, or `undefined` — for a date the forecast does not reach (the
 * route stops at its 3/7/14-day cap) and for any day before `today` (no icon on a past day).
 */
export function weatherForDate(
    weather: CalendarWeather | undefined,
    date: string,
    today?: string
): WeatherDay | undefined {
    if (!weather) return undefined;
    if (today !== undefined && date < today) return undefined;
    return Object.prototype.hasOwnProperty.call(weather.days, date)
        ? weather.days[date]
        : undefined;
}

/** Coordinates rounded to 2 decimals (about 1 km) — the forecast cache's key. */
export function roundCoordinate(value: number): number {
    return Math.round(value * 100) / 100;
}
